import { parse } from '@nostr/tools/nip27'
import { bareNostrUser, loadNostrUser } from '@nostr/gadgets/metadata'
import { Extension } from '@tiptap/core'
import { Node as PMNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

// maps the editor document to the plain text that nip27 parses,
// keeping track of where each text node sits in that plain text.
// atom nodes (mentions, emojis, hard breaks) are replaced by a space
// since they can't be part of any highlightable block anyway.
type Segment = { docPos: number; start: number; end: number }

function buildSource(doc: PMNode): { text: string; segments: Segment[] } {
  const segments: Segment[] = []
  let text = ''

  doc.forEach((paragraph, paragraphOffset, paragraphIndex) => {
    if (paragraphIndex > 0) text += '\n'

    paragraph.forEach((child, childOffset) => {
      if (child.isText && child.text) {
        segments.push({
          docPos: paragraphOffset + 1 + childOffset,
          start: text.length,
          end: text.length + child.text.length
        })
        text += child.text
      } else {
        text += ' '
      }
    })
  })

  return { text, segments }
}

function findSegment(segments: Segment[], offset: number): Segment | undefined {
  let low = 0
  let high = segments.length - 1
  while (low <= high) {
    const mid = (low + high) >> 1
    const seg = segments[mid]
    if (offset < seg.start) high = mid - 1
    else if (offset >= seg.end) low = mid + 1
    else return seg
  }
  return undefined
}

const profileNamePromises = new Map<string, Promise<string>>()

function getProfileName(pubkey: string): Promise<string> {
  let promise = profileNamePromises.get(pubkey)
  if (!promise) {
    if (profileNamePromises.size > 1000) profileNamePromises.clear()
    promise = loadNostrUser(pubkey)
      .then((profile) => profile.shortName)
      .catch(() => bareNostrUser(pubkey).shortName)
    profileNamePromises.set(pubkey, promise)
  }
  return promise
}

function createMentionWidget(pubkey: string) {
  return () => {
    const span = document.createElement('span')
    span.className = 'text-primary'
    span.textContent = '@…'
    void getProfileName(pubkey).then((name) => {
      span.textContent = name
    })
    return span
  }
}

type RefRange = { from: number; to: number }

function buildDecorations(doc: PMNode): { decorations: DecorationSet; refs: RefRange[] } {
  const { text, segments } = buildSource(doc)
  const decorations: Decoration[] = []
  const refs: RefRange[] = []

  for (const block of parse(text)) {
    if (block.type === 'text' || block.type === 'emoji') continue

    const segment = findSegment(segments, block.start)
    // blocks never contain whitespace, so they always sit inside a single text node
    if (!segment || block.end > segment.end) continue

    const from = segment.docPos + block.start - segment.start
    const to = segment.docPos + block.end - segment.start

    if (
      block.type === 'reference' &&
      'pubkey' in block.pointer &&
      !('id' in block.pointer) &&
      !('identifier' in block.pointer)
    ) {
      refs.push({ from, to })
      decorations.push(Decoration.inline(from, to, { class: 'hidden' }))
      decorations.push(
        Decoration.widget(from, createMentionWidget(block.pointer.pubkey), {
          key: `mention-${block.pointer.pubkey}`,
          side: 1
        })
      )
      continue
    }

    decorations.push(
      Decoration.inline(from, to, {
        class:
          block.type === 'relay'
            ? "text-primary before:content-['[ '] after:content-[' ]']"
            : 'text-primary'
      })
    )
  }

  return { decorations: DecorationSet.create(doc, decorations), refs }
}

export const InlineHighlight = Extension.create({
  name: 'inlineHighlight',

  addProseMirrorPlugins() {
    const key = new PluginKey<{ decorations: DecorationSet; refs: RefRange[] }>('inlineHighlight')

    return [
      new Plugin<{ decorations: DecorationSet; refs: RefRange[] }>({
        key,
        state: {
          init: (_, { doc }) => buildDecorations(doc),
          apply: (tr, old) => {
            if (!tr.docChanged) return old
            return buildDecorations(tr.doc)
          }
        },
        props: {
          decorations(state) {
            return key.getState(state)?.decorations
          },
          handleKeyDown(view, event) {
            const refs = key.getState(view.state)?.refs ?? []
            if (refs.length === 0) return false

            const { from, to, empty } = view.state.selection
            if (!empty) return false

            if (event.key === 'ArrowRight') {
              const ref = refs.find((ref) => ref.from === from)
              if (ref) {
                view.dispatch(
                  view.state.tr.setSelection(TextSelection.create(view.state.doc, ref.to))
                )
                return true
              }
            } else if (event.key === 'ArrowLeft') {
              const ref = refs.find((ref) => ref.to === to)
              if (ref) {
                view.dispatch(
                  view.state.tr.setSelection(TextSelection.create(view.state.doc, ref.from))
                )
                return true
              }
            } else if (event.key === 'Backspace') {
              const ref = refs.find((ref) => ref.to === from)
              if (ref) {
                view.dispatch(view.state.tr.delete(ref.from, ref.to))
                return true
              }
            } else if (event.key === 'Delete') {
              const ref = refs.find((ref) => ref.from === from)
              if (ref) {
                view.dispatch(view.state.tr.delete(ref.from, ref.to))
                return true
              }
            }

            return false
          }
        }
      })
    ]
  }
})
